/**
 * money.ts — tipe & aritmetika DESIMAL EKSAK untuk `MONEY` · `QTY` · `RATE`.
 *
 * Sumber kebenaran: ../../design/SCHEMA-v0.md §2 (tipe logis), §6 (pesanan),
 * §7 (tender & invarian), §8 (pajak), §9 (resep/stok) · Guardrail #1 · P1 · P3 · P4 · P6 · P8.
 *
 * ⛔ SATU ATURAN YANG MENAUNGI SELURUH BERKAS INI (CLAUDE.md Guardrail #1):
 *    **uang & kuantitas = desimal eksak, TAK PERNAH float.**
 *    Percobaan terdahulu (`osbhqpos`) melanggarnya di 68 kolom.
 *
 * Bukti `number` tidak cukup (P1): `Number.MAX_SAFE_INTEGER / 10^4` = 900.719.925.474,0991.
 * Artinya `MONEY` berbasis `number` mulai kehilangan presisi di sekitar **Rp900 miliar** —
 * angka yang bisa dicapai akumulasi penjualan grup 47 outlet (§1, tenant Arena).
 *
 * ### Kenapa `bigint` skala-tetap, bukan string atau objek
 * §2 mensyaratkan skema **netral-mesin**: "kalau mesin tak punya (mis. SQLite) → simpan
 * **integer satuan terkecil** + skala tetap". `bigint` persis bentuk itu — satu representasi
 * in-memory yang dipetakan ke `numeric(18,4)` di Postgres dan `INTEGER` di SQLite **tanpa
 * mengubah kontrak**. Ini menutup 🔶 keputusan terbuka #1 dari sisi kode: mesin DB outlet boleh
 * berubah, `money.ts` tidak.
 *
 * ### Nol dependensi runtime
 * Hanya `bigint` bawaan. Dilarang: `zod`, `decimal.js`, `big.js`, `Intl`, `Math.round`,
 * `toFixed`, `parseFloat`, `parseInt`, `Number(...)`, dan literal float.
 * **Dilarang keras menambal `BigInt.prototype.toJSON`** — paket kontrak tidak boleh memutasi
 * global; efeknya menyebar ke lima app dan menyembunyikan kegagalan serialisasi yang justru
 * kita inginkan (lihat `moneyToWire`).
 *
 * ### Catatan penyimpangan dari spesifikasi awal
 * - Spesifikasi menyebut berkas uji `src/money.test.ts`; di repo ini uji hidup di
 *   `test/money.test.ts` (perintah pemilik proyek). Isinya sama.
 * - Merek (brand) memakai `unique symbol` sesuai spesifikasi; tak ada perubahan makna.
 */
const MONEY_ERROR_TAG = '@osbpos/contract:MoneyError';
/**
 * Kesalahan yang dilempar seluruh konstruktor & operator di berkas ini.
 *
 * P6 ("kontrol harus MEMAKSA, bukan sekadar mencatat"): nilai yang melanggar batas ditolak
 * **saat dibuat**, bukan saat kasir menekan Bayar atau saat `INSERT` ditolak DB.
 */
export class MoneyError extends Error {
    /**
     * Penanda runtime. Dipakai {@link isMoneyError} sebagai ganti `instanceof`, yang pecah bila
     * paket kontrak termuat dua kali (dua salinan di dua `node_modules`) — kejadian biasa di
     * monorepo lima app.
     */
    tag = MONEY_ERROR_TAG;
    /** Klasifikasi struktural kesalahan. */
    code;
    /** Masukan mentah yang memicu kesalahan, bila ada (untuk forensik/log). */
    input;
    constructor(code, message, input) {
        super(message);
        this.name = 'MoneyError';
        this.code = code;
        if (input !== undefined) {
            this.input = input;
        }
    }
}
/**
 * Type guard {@link MoneyError} berbasis penanda properti, **bukan `instanceof`**.
 * Aman melintasi batas modul/duplikasi paket.
 */
export function isMoneyError(v) {
    return (typeof v === 'object' &&
        v !== null &&
        v.tag === MONEY_ERROR_TAG);
}
const ROUNDING_MODES = [
    'half_up',
    'half_even',
    'floor',
    'ceil',
    'trunc',
    'exact',
];
/**
 * Pembulatan yang **direkomendasikan** untuk rupiah (§6/§7/§8): setengah menjauhi nol.
 * Ini konstanta bernama yang **dioper eksplisit**, bukan nilai bawaan tanda tangan.
 */
export const ROUNDING_MONEY_DEFAULT = 'half_up';
/** Pembulatan yang direkomendasikan untuk kuantitas (§9 resep/backflush). Sama alasannya. */
export const ROUNDING_QTY_DEFAULT = 'half_up';
function assertRoundingMode(mode) {
    if (!ROUNDING_MODES.includes(mode)) {
        throw new MoneyError('domain', `mode pembulatan tak dikenal: ${String(mode)}`);
    }
}
/**
 * Inti pembulatan — SATU implementasi, dipakai seluruh berkas (Money, Qty, Rate).
 * Menghitung `numerator / denominator` pada bilangan bulat, dibulatkan menurut `mode`.
 *
 * Tanda dinormalkan ke pembilang lebih dulu agar `half_up` benar-benar "menjauhi nol" dan
 * `floor`/`ceil` benar-benar "menuju −∞/+∞" (bukan kecelakaan `Math.round`).
 */
function divRound(numerator, denominator, mode) {
    assertRoundingMode(mode);
    if (denominator === 0n) {
        throw new MoneyError('domain', 'pembagi nol');
    }
    let num = numerator;
    let den = denominator;
    if (den < 0n) {
        num = -num;
        den = -den;
    }
    const q = num / den; // bigint: selalu memotong MENUJU NOL
    const r = num - q * den; // bertanda sama dengan num
    if (r === 0n) {
        return q; // eksak — tak ada pembulatan, `exact` pun lolos
    }
    const step = num < 0n ? -1n : 1n;
    const twice = (r < 0n ? -r : r) * 2n;
    switch (mode) {
        case 'trunc':
            return q;
        case 'floor':
            return num < 0n ? q - 1n : q;
        case 'ceil':
            return num > 0n ? q + 1n : q;
        case 'exact':
            throw new MoneyError('precision', 'nilai tak bisa dinyatakan tanpa pembulatan, sedangkan mode = "exact"');
        case 'half_up':
            return twice >= den ? q + step : q;
        case 'half_even':
            if (twice > den)
                return q + step;
            if (twice < den)
                return q;
            return q % 2n === 0n ? q : q + step;
    }
    /* c8 ignore next */
    throw new MoneyError('domain', `mode pembulatan tak dikenal: ${String(mode)}`);
}
function makeSpec(label, scale, maxDigits) {
    return {
        label,
        scale,
        maxDigits,
        factor: 10n ** BigInt(scale),
        limit: 10n ** BigInt(maxDigits),
        maxIntDigits: maxDigits - scale,
    };
}
/** Jumlah digit pecahan `MONEY` (§2 `numeric(18,4)`). */
export const MONEY_SCALE = 4;
/** Jumlah digit pecahan `QTY` (§2 `numeric(18,6)`). */
export const QTY_SCALE = 6;
/** Jumlah digit pecahan `RATE` (§2 `numeric(9,6)`). */
export const RATE_SCALE = 6;
/** Total digit `MONEY` (§2 `numeric(18,4)`) ⇒ maks 99.999.999.999.999,9999. */
export const MONEY_MAX_DIGITS = 18;
/** Total digit `QTY` (§2 `numeric(18,6)`). */
export const QTY_MAX_DIGITS = 18;
/** Total digit `RATE` (§2 `numeric(9,6)`) ⇒ maks 999,999999. */
export const RATE_MAX_DIGITS = 9;
const MONEY_SPEC = makeSpec('MONEY', MONEY_SCALE, MONEY_MAX_DIGITS);
const QTY_SPEC = makeSpec('QTY', QTY_SCALE, QTY_MAX_DIGITS);
const RATE_SPEC = makeSpec('RATE', RATE_SCALE, RATE_MAX_DIGITS);
/**
 * Batas rentang **bukan hiasan**: ia identik dengan lebar kolom DDL §2, jadi nilai yang lolos
 * konstruksi dijamin muat saat `INSERT`. Melanggar = `MoneyError('range')` saat dibuat (P6).
 */
function assertRange(units, spec, input) {
    if (units >= spec.limit || units <= -spec.limit) {
        throw new MoneyError('range', `${spec.label} di luar rentang numeric(${spec.maxDigits},${spec.scale})`, input);
    }
    return units;
}
/* -------------------------------------------------------------------------- */
/* 5. Parsing & format kanonik                                                */
/* -------------------------------------------------------------------------- */
/**
 * Tata bahasa KETAT. Leniency menyembunyikan bug: `"1,5"` yang diam-diam jadi `1` adalah cara
 * termurah menghilangkan Rp0,5 dari setiap struk sampai ada yang mengaudit.
 *
 * Ditolak tanpa kompromi: `""`, `" 1"`, `"1 "`, `"+1"`, `"1e3"`, `".5"`, `"5."`, `"1,5"`,
 * `"1_0"`, `"007"`, `"0x10"`, `"NaN"`, `"Infinity"`, digit non-ASCII (`"١٢٣"`).
 * `[0-9]` ditulis eksplisit (bukan `\d`) agar niatnya terbaca: hanya digit ASCII.
 */
const DECIMAL_GRAMMAR = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/;
/**
 * Parse string desimal → satuan skala-tetap. **Tanpa float sama sekali**:
 * `units = BigInt(tanda + intPart + fracPart.padEnd(scale, '0'))`.
 *
 * @param rounding `undefined` ⇒ digit pecahan berlebih = `MoneyError('precision')`
 *                 (tak pernah dipotong diam-diam); selain itu dibulatkan menurut mode.
 */
function parseScaled(text, spec, rounding) {
    if (typeof text !== 'string') {
        throw new MoneyError('syntax', `${spec.label} hanya menerima string desimal kanonik`);
    }
    if (!DECIMAL_GRAMMAR.test(text)) {
        throw new MoneyError('syntax', `${spec.label} bukan desimal kanonik`, text);
    }
    const negative = text.charCodeAt(0) === 0x2d; /* '-' */
    const body = negative ? text.slice(1) : text;
    const dot = body.indexOf('.');
    const intPart = dot === -1 ? body : body.slice(0, dot);
    const fracPart = dot === -1 ? '' : body.slice(dot + 1);
    // Validasi lebar bagian bulat SEBELUM BigInt() — mencegah string absurd (10^9 digit)
    // membebani parser sebelum ditolak.
    if (intPart.length > spec.maxIntDigits) {
        throw new MoneyError('range', `${spec.label} di luar rentang numeric(${spec.maxDigits},${spec.scale})`, text);
    }
    let units;
    if (fracPart.length <= spec.scale) {
        const magnitude = BigInt(intPart + fracPart.padEnd(spec.scale, '0'));
        units = negative ? -magnitude : magnitude;
    }
    else {
        if (rounding === undefined) {
            throw new MoneyError('precision', `${spec.label} berskala ${spec.scale}; masukan punya ${fracPart.length} digit pecahan`, text);
        }
        const extra = fracPart.length - spec.scale;
        const magnitude = BigInt(intPart + fracPart);
        units = divRound(negative ? -magnitude : magnitude, 10n ** BigInt(extra), rounding);
    }
    return assertRange(units, spec, text);
}
/**
 * Format kanonik: selalu `scale` digit pecahan, selalu ada titik, `-` hanya untuk negatif,
 * tanpa `+`, tanpa eksponen, tanpa pemisah ribuan.
 *
 * Nol selalu `"0.0000"` — **tak pernah** `"-0.0000"`: `bigint` tak punya −0, jadi jangan
 * menciptakannya saat memformat. Dibentuk dari `abs / factor` dan `abs % factor` + `padStart`;
 * `toFixed` dilarang (G7).
 */
function formatScaled(units, spec) {
    const negative = units < 0n;
    const abs = negative ? -units : units;
    const whole = abs / spec.factor;
    const frac = abs % spec.factor;
    return `${negative ? '-' : ''}${whole.toString()}.${frac.toString().padStart(spec.scale, '0')}`;
}
function tryParseScaled(v, spec) {
    // G3: `number` TAK PERNAH diterima meski nilainya "benar". Payload sync yang mengirim
    // 12345.67 sebagai JSON number ditolak, bukan diselamatkan — karena yang dikirim sudah
    // kehilangan presisi sebelum sampai ke sini.
    if (typeof v !== 'string')
        return undefined;
    try {
        return parseScaled(v, spec, undefined);
    }
    catch {
        return undefined;
    }
}
function isCanonicalString(v, spec) {
    const units = tryParseScaled(v, spec);
    if (units === undefined)
        return false;
    return formatScaled(units, spec) === v;
}
function fromUnits(units, spec) {
    if (typeof units !== 'bigint') {
        throw new MoneyError('domain', `${spec.label} satuan mentah wajib bigint`);
    }
    return assertRange(units, spec);
}
/* -------------------------------------------------------------------------- */
/* 6. Konstruksi & wire — MONEY                                               */
/* -------------------------------------------------------------------------- */
/**
 * Membuat {@link Money} dari string desimal kanonik. Melempar {@link MoneyError}.
 *
 * ⚠️ Digit pecahan lebih dari 4 = `precision`, **bukan** dipotong diam-diam. Kalau memang perlu
 * menerima presisi lebih tinggi, itu keputusan sadar ⇒ pakai {@link parseMoneyRounded}.
 *
 * @example money('12345.67') // → 12345.6700
 */
export function money(text) {
    return parseScaled(text, MONEY_SPEC, undefined);
}
/**
 * Versi non-melempar {@link money} untuk data **TAK TEPERCAYA** (payload sync §10, input UI).
 * Mengembalikan `undefined` untuk apa pun yang bukan string desimal kanonik yang muat.
 *
 * G3: `1`, `1.5`, `1n`, `null`, `{}`, `[]`, `true` ⇒ `undefined`. Hanya `string` yang lolos.
 */
export function tryMoney(v) {
    return tryParseScaled(v, MONEY_SPEC);
}
/**
 * Satu-satunya jalan menerima string dengan lebih dari 4 digit pecahan sebagai {@link Money}.
 * Mode `'exact'` menjadikannya setara {@link money} (melempar `precision` bila perlu membulatkan).
 */
export function parseMoneyRounded(text, mode) {
    return parseScaled(text, MONEY_SPEC, mode);
}
/**
 * {@link Money} → bentuk WIRE (§10 amplop sync, §10b peristiwa). Selalu 4 digit pecahan.
 *
 * ### G4 — `bigint` tidak JSON-serializable, dan itu FITUR
 * `JSON.stringify({ amount: money('1') })` melempar `TypeError`. Kegagalan keras di jalur yang
 * salah jauh lebih baik daripada `"1"` di satu lapis dan `1` di lapis lain yang menyimpang
 * diam-diam. Karena itu **dilarang menambal `BigInt.prototype.toJSON`** (G5).
 */
export function moneyToWire(m) {
    return formatScaled(m, MONEY_SPEC);
}
/** Apakah `v` string {@link MoneyString} kanonik (tepat 4 digit pecahan, muat di `numeric(18,4)`)? */
export function isMoneyString(v) {
    return isCanonicalString(v, MONEY_SPEC);
}
/**
 * Jembatan driver DB (§2 "simpan integer satuan terkecil + skala tetap", mis. SQLite `INTEGER`).
 * G8: driver menerima string kanonik ({@link moneyToWire}) atau `bigint` — **tak pernah** `Number`.
 *
 * ⛔ Menolak satuan berskala LAIN **saat kompilasi** ({@link MoneyUnitsInput}): `Qty`, `Rate`,
 * `qtyToScaledUnits(...)`, dan `rateToScaledUnits(...)` tak bisa masuk ke sini. Kalau tidak,
 * skala 6 yang menyamar jadi skala 4 lolos tanpa suara dan nilainya meleset 100× — `fromUnits`
 * hanya memeriksa rentang (P1/P6).
 */
export function moneyFromScaledUnits(units) {
    return fromUnits(units, MONEY_SPEC);
}
/**
 * Satuan skala-tetap mentah dari sebuah {@link Money} (skala 4). Lihat {@link moneyFromScaledUnits}.
 *
 * Hasilnya ber-merek {@link MoneyUnits} — bukan `bigint` polos — supaya ia **tak bisa** dioper ke
 * {@link qtyFromScaledUnits}/{@link rateFromScaledUnits}. Untuk driver DB nilainya tetap `bigint`
 * biasa saat runtime; mereknya hanya hidup di tipe.
 */
export function moneyToScaledUnits(m) {
    return m;
}
/* -------------------------------------------------------------------------- */
/* 7. Konstruksi & wire — QTY                                                 */
/* -------------------------------------------------------------------------- */
/** Membuat {@link Qty} dari string desimal kanonik (skala 6, §2). Melempar {@link MoneyError}. */
export function qty(text) {
    return parseScaled(text, QTY_SPEC, undefined);
}
/** Versi non-melempar {@link qty} untuk data tak tepercaya. G3 berlaku sama. */
export function tryQty(v) {
    return tryParseScaled(v, QTY_SPEC);
}
/** Satu-satunya jalan menerima >6 digit pecahan sebagai {@link Qty}. */
export function parseQtyRounded(text, mode) {
    return parseScaled(text, QTY_SPEC, mode);
}
/** {@link Qty} → bentuk WIRE. Selalu 6 digit pecahan. G4/G6 berlaku sama. */
export function qtyToWire(q) {
    return formatScaled(q, QTY_SPEC);
}
/** Apakah `v` string {@link QtyString} kanonik (tepat 6 digit pecahan)? */
export function isQtyString(v) {
    return isCanonicalString(v, QTY_SPEC);
}
/** Jembatan driver DB untuk {@link Qty}. G8. Menolak satuan berskala lain — {@link QtyUnitsInput}. */
export function qtyFromScaledUnits(units) {
    return fromUnits(units, QTY_SPEC);
}
/** Satuan skala-tetap mentah dari sebuah {@link Qty} (skala 6). Ber-merek — lihat {@link MoneyUnits}. */
export function qtyToScaledUnits(q) {
    return q;
}
/* -------------------------------------------------------------------------- */
/* 8. Konstruksi & wire — RATE                                                */
/* -------------------------------------------------------------------------- */
/**
 * Membuat {@link Rate} dari string desimal kanonik (skala 6, §2).
 *
 * ⚠️ **Tak ada `ratePercent('10')`.** §2 & §8 eksplisit: "simpan pecahan, bukan 11".
 * 10% ditulis `rate('0.10')`. Konversi persen↔pecahan adalah urusan UI, bukan kontrak —
 * begitu kontrak menerima dua satuan, salah satunya akan dipakai di tempat yang salah.
 */
export function rate(text) {
    return parseScaled(text, RATE_SPEC, undefined);
}
/** Versi non-melempar {@link rate} untuk data tak tepercaya. G3 berlaku sama. */
export function tryRate(v) {
    return tryParseScaled(v, RATE_SPEC);
}
/** Satu-satunya jalan menerima >6 digit pecahan sebagai {@link Rate}. */
export function parseRateRounded(text, mode) {
    return parseScaled(text, RATE_SPEC, mode);
}
/** {@link Rate} → bentuk WIRE. Selalu 6 digit pecahan. G4/G6 berlaku sama. */
export function rateToWire(r) {
    return formatScaled(r, RATE_SPEC);
}
/** Apakah `v` string {@link RateString} kanonik (tepat 6 digit pecahan, muat di `numeric(9,6)`)? */
export function isRateString(v) {
    return isCanonicalString(v, RATE_SPEC);
}
/** Jembatan driver DB untuk {@link Rate}. G8. Menolak satuan berskala lain — {@link RateUnitsInput}. */
export function rateFromScaledUnits(units) {
    return fromUnits(units, RATE_SPEC);
}
/** Satuan skala-tetap mentah dari sebuah {@link Rate} (skala 6). Ber-merek — lihat {@link MoneyUnits}. */
export function rateToScaledUnits(r) {
    return r;
}
/* -------------------------------------------------------------------------- */
/* 9. Konstanta nilai                                                         */
/* -------------------------------------------------------------------------- */
/** `0.0000` — nilai netral penjumlahan uang; hasil {@link sumMoney} atas array kosong. */
export const MONEY_ZERO = 0n;
/** `0.000000` — nilai netral penjumlahan kuantitas. */
export const QTY_ZERO = 0n;
/** `0.000000` — tarif nol (mis. baris cuma-cuma §8/K10). */
export const RATE_ZERO = 0n;
/**
 * `1.000000` — wajib ada untuk penyebut pajak INKLUSIF (§8 `is_inclusive`): `G × r / (1 + r)`.
 * Lihat {@link mulDivMoney}.
 */
export const RATE_ONE = 1000000n;
/* -------------------------------------------------------------------------- */
/* 10. Aritmetika MONEY                                                       */
/* -------------------------------------------------------------------------- */
/**
 * Penjumlahan uang — **eksak menurut definisi**, karena itu tak menerima {@link RoundingMode}.
 * Melempar `range` bila hasil melampaui `numeric(18,4)` (P6: gagal keras, bukan wrap diam-diam).
 */
export function addMoney(a, b) {
    return assertRange(a + b, MONEY_SPEC);
}
/** Pengurangan uang — eksak. Melempar `range` bila hasil melampaui `numeric(18,4)`. */
export function subMoney(a, b) {
    return assertRange(a - b, MONEY_SPEC);
}
/** Negasi uang (mis. refund/pembalikan §7 `is_reversed`) — eksak. */
export function negMoney(a) {
    return assertRange(-a, MONEY_SPEC);
}
/** Nilai mutlak uang — eksak. */
export function absMoney(a) {
    return (a < 0n ? -a : a);
}
/**
 * Menjumlahkan daftar uang. `[]` ⇒ {@link MONEY_ZERO}.
 *
 * Hasil **tak bergantung urutan** karena `bigint` eksak — itulah yang membuat
 * `order.tax_total = Σ order_line_tax.tax_amount` (§8) stabil. Implementasi berbasis float
 * gagal di titik ini dan gagalnya baru terlihat berminggu-minggu kemudian.
 *
 * Batas rentang diperiksa pada hasil akhir; nilai antara tetap eksak (bigint tak meluap), jadi
 * `[MAX, 0.0001, -0.0001]` sah — dan itu memang benar secara aritmetika.
 */
export function sumMoney(xs) {
    let total = 0n;
    for (const x of xs)
        total += x;
    return assertRange(total, MONEY_SPEC);
}
/**
 * `unit × qty` → uang (§6 `order_line.line_total` dari `unit_price_gross` × `qty`).
 * Satu pembulatan, mode wajib eksplisit.
 */
export function mulMoneyByQty(unit, q, mode) {
    return assertRange(divRound(unit * q, QTY_SPEC.factor, mode), MONEY_SPEC);
}
/**
 * `base × rate` → uang. Dipakai §8 pajak EKSKLUSIF (`tax_amount` dari `taxable_base`) dan
 * §7 `mdr_percent` (**beban acquirer, BUKAN diskon** — P3).
 * Satu pembulatan, mode wajib eksplisit.
 */
export function mulMoneyByRate(base, r, mode) {
    return assertRange(divRound(base * r, RATE_SPEC.factor, mode), MONEY_SPEC);
}
/**
 * `v × num / den` dengan **SATU** pembulatan di akhir. Primitif untuk pajak INKLUSIF
 * (§8 `is_inclusive`, umum di F&B Indonesia).
 *
 * ### Rumus (§8) untuk harga bruto `G` yang sudah termasuk pajak bertarif `r`
 * ```
 * tax  = G × r / (1 + r)
 * base = G − tax          ← DIKURANGKAN, bukan dihitung ulang
 * ```
 * ```ts
 * const tax  = mulDivMoney(G, r, addRate(RATE_ONE, r), ROUNDING_MONEY_DEFAULT);
 * const base = subMoney(G, tax);
 * ```
 *
 * ### Jebakan pembulatan
 * 1. **Jangan hitung `base` dan `tax` masing-masing lalu bulatkan dua-duanya.**
 *    `round(G/(1+r)) + round(G·r/(1+r))` meleset 1 satuan dari `G` untuk banyak nilai. `G` adalah
 *    yang dibayar tamu; `base + tax` **wajib** = `G` eksak, karena `grand_total` inklusif ikut ke
 *    invarian §7 (`Σ tender = grand_total`).
 * 2. **Jangan komposisikan {@link mulMoneyByRate} lalu {@link divMoneyByQty}** — itu dua pembulatan.
 * 3. **Lebih dari satu pajak inklusif** (§8 `sequence` + `base_components`): penyebutnya
 *    `1 + Σ tarif efektif` ({@link sumRate}), diurai **sekali** menjadi total pajak, lalu dipecah
 *    per `tax_code` dengan {@link allocateMoney}. Menghitung tiap pajak sendiri-sendiri membuat
 *    Σ pajak ≠ (G − base).
 * 4. **Baris cuma-cuma** (§8, K10): `exempt_reason` terisi ⇒ **tak ada** baris `order_line_tax`;
 *    jangan mengurai apa pun, `base = G`.
 *
 * ⚠️ `money.ts` sengaja **tidak** mengekspor `splitInclusive()`: rumusnya kebijakan pajak (§8,
 * milik mesin pajak), bukan kontrak nilai. Yang hidup di sini hanya primitifnya.
 */
export function mulDivMoney(v, num, den, mode) {
    if (den === 0n) {
        throw new MoneyError('domain', 'mulDivMoney: penyebut nol');
    }
    return assertRange(divRound(v * num, den, mode), MONEY_SPEC);
}
/**
 * `total ÷ qty` → harga satuan (mis. menurunkan `unit_price_gross` dari total combo §6
 * `combo_parent_line_id`). Satu pembulatan, mode wajib eksplisit. `q = 0` ⇒ `domain`.
 */
export function divMoneyByQty(total, q, mode) {
    if (q === 0n) {
        throw new MoneyError('domain', 'divMoneyByQty: kuantitas nol');
    }
    return assertRange(divRound(total * QTY_SPEC.factor, q, mode), MONEY_SPEC);
}
/**
 * Membulatkan uang ke `decimals` digit pecahan (0..4), hasilnya tetap {@link Money} skala 4.
 *
 * ⚠️ **Bulatkan SEKALI per kolom tersimpan.** Nilai antara wajib tetap presisi penuh — itulah
 * gunanya {@link mulDivMoney}.
 * ⚠️ **Bulatkan di tingkat BARIS, jumlahkan ke atas.** §8 mensyaratkan snapshot pajak per baris
 * (`order_line_tax`) ⇒ `order.tax_total` **wajib** = {@link sumMoney} atas `tax_amount` per baris,
 * **tak pernah** dihitung ulang dari `subtotal × rate`. Menghitung ulang di tingkat pesanan
 * menghasilkan selisih 1–2 rupiah terhadap struk yang sudah dicetak.
 */
export function roundMoneyToDecimals(m, decimals, mode) {
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > MONEY_SCALE) {
        throw new MoneyError('domain', `roundMoneyToDecimals: decimals harus 0..${MONEY_SCALE}`);
    }
    const factor = 10n ** BigInt(MONEY_SCALE - decimals);
    return assertRange(divRound(m, factor, mode) * factor, MONEY_SPEC);
}
/**
 * Membulatkan uang ke kelipatan `step` — **pembulatan TUNAI** (mis. Rp100 karena pecahan
 * Rp1/Rp50 tak beredar).
 *
 * ⚠️ **Ini bukan pembulatan hitung.** Hanya boleh dipakai pada nilai yang **ditagihkan**, dan
 * selisihnya **wajib disimpan sebagai nilai tersendiri** (P3: jangan pernah menyimpan net saja).
 * Selisih yang hilang = `Σ order_tender.amount ≠ order.grand_total` ⇒ invarian §7 pecah.
 *
 * `step <= 0` ⇒ `domain`.
 */
export function roundMoneyToMultiple(m, step, mode) {
    if (step <= 0n) {
        throw new MoneyError('domain', 'roundMoneyToMultiple: step wajib > 0');
    }
    return assertRange(divRound(m, step, mode) * step, MONEY_SPEC);
}
/* -------------------------------------------------------------------------- */
/* 11. Perbandingan MONEY                                                     */
/* -------------------------------------------------------------------------- */
function cmpUnits(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Perbandingan tiga arah dua {@link Money}. */
export function cmpMoney(a, b) {
    return cmpUnits(a, b);
}
/** `a == b` */
export function eqMoney(a, b) {
    return a === b;
}
/** `a < b` */
export function ltMoney(a, b) {
    return a < b;
}
/** `a <= b` */
export function lteMoney(a, b) {
    return a <= b;
}
/** `a > b` */
export function gtMoney(a, b) {
    return a > b;
}
/** `a >= b` */
export function gteMoney(a, b) {
    return a >= b;
}
/** Nilai terkecil; bila sama, mengembalikan operan PERTAMA. */
export function minMoney(a, b) {
    return b < a ? b : a;
}
/** Nilai terbesar; bila sama, mengembalikan operan PERTAMA. */
export function maxMoney(a, b) {
    return b > a ? b : a;
}
/** Tanda: `-1` negatif, `0` nol, `1` positif. */
export function signMoney(m) {
    return cmpUnits(m, 0n);
}
/** Apakah tepat nol? */
export function isZeroMoney(m) {
    return m === 0n;
}
/** Apakah > 0? */
export function isPositiveMoney(m) {
    return m > 0n;
}
/** Apakah < 0? (mis. selisih pembulatan tunai yang membulat ke bawah) */
export function isNegativeMoney(m) {
    return m < 0n;
}
/* -------------------------------------------------------------------------- */
/* 12. Aritmetika & perbandingan QTY                                          */
/* -------------------------------------------------------------------------- */
/** Penjumlahan kuantitas — eksak (§9 `stock_movement` append-only). */
export function addQty(a, b) {
    return assertRange(a + b, QTY_SPEC);
}
/** Pengurangan kuantitas — eksak. */
export function subQty(a, b) {
    return assertRange(a - b, QTY_SPEC);
}
/** Negasi kuantitas (mis. `transfer_out` vs `transfer_in`, §9) — eksak. */
export function negQty(a) {
    return assertRange(-a, QTY_SPEC);
}
/** Nilai mutlak kuantitas — eksak. */
export function absQty(a) {
    return (a < 0n ? -a : a);
}
/** Menjumlahkan daftar kuantitas. `[]` ⇒ {@link QTY_ZERO}. Tak bergantung urutan. */
export function sumQty(xs) {
    let total = 0n;
    for (const x of xs)
        total += x;
    return assertRange(total, QTY_SPEC);
}
/**
 * `a × b` → kuantitas, satu pembulatan (§9: `recipe_line.qty` × `order_line.qty` untuk
 * `sale_backflush`).
 *
 * ⚠️ P8 berlaku di pemakainya, bukan di sini: **bila resep tak ada, jangan terbitkan
 * `sale_backflush` kosong** — terbitkan alarm cakupan. Bukti: VTEC menerbitkan 135.593 dokumen
 * konsumsi bahan, **92% kosong**, terus-menerus sampai Juli 2026 (§8j/§9).
 */
export function mulQtyByQty(a, b, mode) {
    return assertRange(divRound(a * b, QTY_SPEC.factor, mode), QTY_SPEC);
}
/**
 * `a ÷ b` → kuantitas (§9: bahan per resep dibagi `recipe.yield_qty` — "satu adonan jadi 20 roti").
 *
 * ⛔ Ada di sini, bukan di modul stok: seluruh aritmetika kuantitas hidup di satu berkas supaya
 * pembulatannya tak bisa berbeda antara dua pemakai. `b = 0` ⇒ `domain` — resep dengan yield nol
 * bukan resep, dan membiarkannya lolos menghasilkan pembagian tak hingga di angka stok.
 */
export function divQtyByQty(a, b, mode) {
    if (b === 0n) {
        throw new MoneyError('domain', 'divQtyByQty: penyebut nol');
    }
    return assertRange(divRound(a * QTY_SPEC.factor, b, mode), QTY_SPEC);
}
/** Membulatkan kuantitas ke `decimals` digit pecahan (0..6); hasil tetap skala 6. */
export function roundQtyToDecimals(q, decimals, mode) {
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > QTY_SCALE) {
        throw new MoneyError('domain', `roundQtyToDecimals: decimals harus 0..${QTY_SCALE}`);
    }
    const factor = 10n ** BigInt(QTY_SCALE - decimals);
    return assertRange(divRound(q, factor, mode) * factor, QTY_SPEC);
}
/** Perbandingan tiga arah dua {@link Qty}. */
export function cmpQty(a, b) {
    return cmpUnits(a, b);
}
/** `a == b` */
export function eqQty(a, b) {
    return a === b;
}
/** Tanda kuantitas: `-1` / `0` / `1`. */
export function signQty(q) {
    return cmpUnits(q, 0n);
}
/** Apakah tepat nol? */
export function isZeroQty(q) {
    return q === 0n;
}
/** Kuantitas terkecil; bila sama, mengembalikan operan PERTAMA. */
export function minQty(a, b) {
    return b < a ? b : a;
}
/** Kuantitas terbesar; bila sama, mengembalikan operan PERTAMA. */
export function maxQty(a, b) {
    return b > a ? b : a;
}
/* -------------------------------------------------------------------------- */
/* 13. Aritmetika & perbandingan RATE                                         */
/* -------------------------------------------------------------------------- */
/** Penjumlahan tarif — eksak. Dipakai membentuk penyebut `1 + r` pajak inklusif (§8). */
export function addRate(a, b) {
    return assertRange(a + b, RATE_SPEC);
}
/** Pengurangan tarif — eksak. */
export function subRate(a, b) {
    return assertRange(a - b, RATE_SPEC);
}
/**
 * Menjumlahkan daftar tarif. `[]` ⇒ {@link RATE_ZERO}.
 *
 * **Wajib ada**: bila §8 memasang lebih dari satu pajak inklusif, penyebutnya adalah
 * `1 + Σ tarif efektif` — dihitung sekali, bukan berlapis. Lihat {@link mulDivMoney} jebakan #3.
 */
export function sumRate(xs) {
    let total = 0n;
    for (const x of xs)
        total += x;
    return assertRange(total, RATE_SPEC);
}
/** Perbandingan tiga arah dua {@link Rate}. */
export function cmpRate(a, b) {
    return cmpUnits(a, b);
}
/** `a == b` */
export function eqRate(a, b) {
    return a === b;
}
/** Apakah tarif tepat nol? (mis. baris cuma-cuma §8/K10) */
export function isZeroRate(r) {
    return r === 0n;
}
/* -------------------------------------------------------------------------- */
/* 14. Alokasi                                                                */
/* -------------------------------------------------------------------------- */
function itemAt(xs, i) {
    const v = xs[i];
    if (v === undefined) {
        throw new MoneyError('domain', `indeks alokasi ${String(i)} di luar jangkauan`);
    }
    return v;
}
/**
 * Inti alokasi — metode **sisa terbesar** (largest remainder), deterministik penuh.
 *
 * ```
 * base_i = (T × w_i) / W          (bigint, memotong menuju nol)
 * rem_i  = T × w_i − base_i × W
 * leftover = T − Σ base_i         (|leftover| < n)
 * urut indeks menurut (|rem_i| MENURUN, lalu index MENAIK) — tie-break eksplisit
 * tambahkan `step` ke |leftover| indeks pertama
 * ```
 *
 * Pembanding menyertakan indeks, jadi hasilnya tak bergantung stabilitas
 * `Array.prototype.sort`.
 */
function allocateUnits(total, weights) {
    const n = weights.length;
    if (n < 1) {
        throw new MoneyError('domain', 'alokasi butuh minimal satu bobot');
    }
    let totalWeight = 0n;
    for (const w of weights) {
        if (w < 0n) {
            throw new MoneyError('domain', 'bobot alokasi tak boleh negatif');
        }
        totalWeight += w;
    }
    if (totalWeight === 0n) {
        // P8 dalam bentuk aritmetika: JANGAN terbitkan dokumen turunan bila masukannya tak ada.
        // Membagi rata diam-diam di atas dasar kosong = angka yang tampak benar tanpa alasan.
        // Kasus "memang mau rata" punya fungsinya sendiri: allocateMoneyEvenly.
        throw new MoneyError('domain', 'Σ bobot alokasi = 0; tak ada dasar pembagian');
    }
    const parts = new Array(n);
    const remainders = new Array(n);
    let assigned = 0n;
    for (let i = 0; i < n; i += 1) {
        const w = itemAt(weights, i);
        const numerator = total * w;
        const base = numerator / totalWeight; // memotong menuju nol
        parts[i] = base;
        remainders[i] = numerator - base * totalWeight;
        assigned += base;
    }
    const leftover = total - assigned; // |leftover| < n
    if (leftover !== 0n) {
        const step = leftover < 0n ? -1n : 1n;
        let bumps = leftover < 0n ? -leftover : leftover;
        const order = new Array(n);
        for (let i = 0; i < n; i += 1)
            order[i] = i;
        order.sort((ia, ib) => {
            const ra0 = itemAt(remainders, ia);
            const rb0 = itemAt(remainders, ib);
            const ra = ra0 < 0n ? -ra0 : ra0;
            const rb = rb0 < 0n ? -rb0 : rb0;
            if (ra > rb)
                return -1;
            if (ra < rb)
                return 1;
            return ia - ib; // tie-break: indeks terkecil lebih dulu
        });
        for (let k = 0; k < n && bumps > 0n; k += 1) {
            const idx = order[k];
            if (idx === undefined)
                break;
            parts[idx] = itemAt(parts, idx) + step;
            bumps -= 1n;
        }
    }
    return parts;
}
/**
 * Membagi `total` ke beberapa bagian menurut bobot uang, **tanpa menghilangkan atau menciptakan
 * satu satuan pun**: `sumMoney(allocateMoney(T, w)) === T` selalu dan eksak.
 *
 * ### Kenapa ini dibutuhkan (konsekuensi skema, bukan kenyamanan)
 * 1. **Diskon tingkat pesanan → baris.** §6 menyimpan `order.discount_total`, tetapi §8
 *    menetapkan `dpp_basis = after_discount` dan menyimpan pajak **per baris**
 *    (`order_line_tax.taxable_base`). Dasar pengenaan per baris mustahil dihitung tanpa menyebar
 *    diskon pesanan ke baris; menyebar dengan pembulatan per baris membuat Σbaris ≠ total diskon
 *    ⇒ `grand_total` meleset ⇒ invarian §7 pecah.
 * 2. **Pajak inklusif.** Total pajak diurai sekali dari harga bruto lalu dialokasikan ke baris /
 *    ke tiap `tax_code` (§8 `sequence`); Σ hasil wajib identik dengan total yang diurai.
 * 3. **Split bill / combo.** Harga combo menempel ke baris induk (§6 `combo_parent_line_id`),
 *    tetapi komponen tetap butuh nilai untuk backflush stok (§9).
 *
 * Bekerja untuk `total` negatif (refund) dengan mencerminkan tanda. Bobot nol ⇒ bagian nol.
 * Bobot negatif, `Σ bobot = 0`, atau daftar kosong ⇒ `MoneyError('domain')`.
 */
export function allocateMoney(total, weights) {
    return allocateUnits(total, weights).map((p) => assertRange(p, MONEY_SPEC));
}
/**
 * Sama dengan {@link allocateMoney}, tetapi bobotnya {@link Qty} — kasus paling sering di §6/§9:
 * menyebar diskon atau nilai combo menurut kuantitas baris.
 */
export function allocateMoneyByQty(total, weights) {
    return allocateUnits(total, weights).map((p) => assertRange(p, MONEY_SPEC));
}
/**
 * Membagi `total` menjadi `parts` bagian **serata mungkin**; sisa satuan jatuh ke indeks terkecil.
 * Ini satu-satunya tempat "bagi rata" boleh terjadi — {@link allocateMoney} sengaja menolaknya
 * saat Σ bobot = 0 (P8).
 *
 * G1: `parts` adalah **pencacah**, bukan nilai. Wajib `Number.isSafeInteger(parts) && parts > 0`,
 * selain itu `MoneyError('domain')`.
 */
export function allocateMoneyEvenly(total, parts) {
    if (!Number.isSafeInteger(parts) || parts <= 0) {
        throw new MoneyError('domain', 'allocateMoneyEvenly: parts wajib bilangan bulat aman > 0');
    }
    const weights = new Array(parts).fill(1n);
    return allocateUnits(total, weights).map((p) => assertRange(p, MONEY_SPEC));
}
//# sourceMappingURL=money.js.map